import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { FIELDS as FF } from "@/lib/fields";
import { EditableField } from "@/components/EditableField";
import { toast } from "sonner";
import {
  Loader2, Upload, Flag, Check, Eye, Plus, Info, FolderOpen, FileText, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { IncomingFiles, AllFiles, DocumentCheckPanel, RequestDocsButton } from "@/components/DocumentsExtra";
import { BulkUpload } from "@/components/BulkUpload";
import { useAuth } from "@/context/AuthContext";
import { Info as Hint } from "@/components/Info";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  received: { label: "Received", cls: "bg-[#159A50]/10 text-[#159A50] border-[#159A50]/30" },
  missing: { label: "Missing", cls: "bg-[#F7F7F7] text-[#6B7280] border-[#E2E2E2]" },
  needs_reupload: { label: "Needs re-upload", cls: "bg-[#CA1212]/10 text-[#CA1212] border-[#CA1212]/30" },
  partial: { label: "Partial", cls: "bg-[#F5B71D]/15 text-[#92600a] border-[#F5B71D]/40" },
  reuploaded_check: { label: "Re-uploaded: check", cls: "bg-[#38A2FF]/10 text-[#1e7fd6] border-[#38A2FF]/30" },
};

const TOGGLES = [
  ["on_rent", "Lives on rent / Aadhaar address not current"],
  ["marriage_mismatch", "Spouse surname / address mismatch"],
  ["running_loans", "Running loans"],
  ["gst_registered", "GST registered"],
];

const APPLICANT_LABEL = { borrower: "Borrower", coborrower: "Co-borrower", company: "Company" };

export function DocumentsTab({ c, recordId, saveField, canEdit }) {
  const { user } = useAuth();
  const [cl, setCl] = useState(null);
  const [selects, setSelects] = useState({});
  const [loading, setLoading] = useState(true);
  const [waLink, setWaLink] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reqSignal, setReqSignal] = useState(0);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [chk, meta, dmeta] = await Promise.all([
        api.get(`/cases/${recordId}/documents-checklist`),
        api.get("/meta/selects"),
        api.get("/documents/meta"),
      ]);
      setCl(chk.data);
      setSelects({ ...(meta.data.selects || {}), ...(dmeta.data.selects || {}) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [recordId]); // eslint-disable-line
  useEffect(() => { api.get(`/cases/${recordId}/whatsapp`).then((r) => setWaLink(r.data.customer_open_link || null)).catch(() => {}); }, [recordId]);

  const setProfile = async (fieldId, value) => {
    const ok = await saveField(fieldId, value);
    if (ok) load(true);
  };
  const setToggle = async (key, val) => {
    try {
      const { data } = await api.post(`/cases/${recordId}/documents/toggles`, { [key]: val });
      setCl(data);
    } catch (e) { toast.error("Failed to save toggle"); }
  };

  if (loading || !cl) {
    return (
      <div className="flex items-center gap-2 text-[#6B7280] text-sm py-16 justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FF4802]" /> Loading documents…
      </div>
    );
  }
  const profileOpts = ["Salaried", "Proprietor", "Director"];
  const payOpts = selects[FF.DIRECTOR_PAY_TYPE] || ["Salary", "Remuneration"];
  const hasProgress = ["borrower", "coborrower", "company"].some((ap) => (cl.progress?.[ap]?.total || 0) > 0);

  return (
    <div className="grid grid-cols-12 gap-6" data-testid="documents-tab">
      <div className="col-span-12 lg:col-span-8 space-y-6">
        <div className={`rounded-2xl border p-5 ${(cl.needs_profile || cl.needs_co_profile || cl.needs_pay_type) ? "border-[#F5B71D]/40 bg-[#F5B71D]/5" : "border-[#E2E2E2] bg-white shadow-sm"}`} data-testid="profile-setup">
          <h3 className="text-sm font-medium text-[#333333] mb-1">{(cl.needs_profile || cl.needs_co_profile || cl.needs_pay_type) ? "Set up the document list" : "Borrower profiles"} <Hint k="borrower_profile" /></h3>
          <p className={`text-xs mb-4 ${(cl.needs_profile || cl.needs_co_profile || cl.needs_pay_type) ? "text-[#92600a]" : "text-[#6B7280]"}`}>
            {(cl.needs_profile || cl.needs_co_profile || cl.needs_pay_type) ? "Pick the borrower profile to generate the right checklist." : "Change a profile to regenerate the checklist."}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ProfileSelect label="Borrower Profile" value={cl.borrower_profile} options={profileOpts}
              disabled={!canEdit} testid="pick-borrower-profile"
              onPick={(v) => setProfile(FF.BORROWER_PROFILE, v)} />
            {cl.has_coborrower && (
              <ProfileSelect label="Co-Borrower Profile" value={cl.coborrower_profile} options={profileOpts}
                disabled={!canEdit} testid="pick-coborrower-profile"
                onPick={(v) => setProfile(FF.CO_BORROWER_PROFILE, v)} />
            )}
            {cl.is_director && (
              <ProfileSelect label="Director Pay Type" value={cl.director_pay_type} options={payOpts}
                disabled={!canEdit} testid="pick-pay-type"
                onPick={(v) => setProfile(FF.DIRECTOR_PAY_TYPE, v)} />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm">
          <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>Case toggles <Hint k="case_toggles" /></h3>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {TOGGLES.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-[#333333] cursor-pointer" data-testid={`toggle-${k}`}>
                <input type="checkbox" className="accent-[#FF4802] w-4 h-4" disabled={!canEdit}
                  checked={!!cl.toggles?.[k]} onChange={(e) => setToggle(k, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="px-1" data-testid="status-legend-row"><span className="text-[11px] text-[#6B7280] flex items-center gap-1 w-full" data-testid="status-legend">Status chips <Hint k="status_chips" /><span className="text-[#C5C4C4]">· Received · Verified · Needs re-upload · Partial · Missing</span></span></div>
        {hasProgress && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="doc-progress">
          {["borrower", "coborrower", "company"].map((ap) => {
            const p = cl.progress?.[ap];
            if (!p || p.total === 0) return null;
            const name = ap === "borrower" ? (c.fields?.[FF.BORROWER] || "Borrower")
              : ap === "coborrower" ? (c.fields?.[FF.CO_BORROWER] || "Co-borrower") : "Company";
            return (
              <span key={ap} className="text-[#6B7280]">
                <span className="font-medium text-[#333333]">{name}</span>{" "}
                <span className={p.done === p.total ? "text-[#159A50] font-semibold" : "text-[#FF4802] font-semibold"}>{p.done}/{p.total}</span>
              </span>
            );
          })}
        </div>
        )}

        {["borrower", "coborrower", "company"].map((ap) => {
          const items = cl.applicants?.[ap] || [];
          if (items.length === 0) return null;
          const title = ap === "borrower" ? `For you (${c.fields?.[FF.BORROWER] || "Borrower"})`
            : ap === "coborrower" ? `For ${c.fields?.[FF.CO_BORROWER] || "co-borrower"}` : "Company documents";
          return (
            <ApplicantSection key={ap} title={title} items={items} recordId={recordId}
              canEdit={canEdit} onChanged={setCl} reload={load} />
          );
        })}

        {canEdit && <AddCustom recordId={recordId} hasCo={cl.has_coborrower} isDirector={cl.is_director} onAdded={setCl} />}

        {canEdit && (
          <div className="flex items-center justify-end gap-2" data-testid="request-docs-row"><Hint k="request_docs" />
            <RequestDocsButton recordId={recordId} c={c} cl={cl} waLink={waLink} user={user} openSignal={reqSignal} />
          </div>
        )}

        {canEdit && <BulkUpload recordId={recordId} onDone={() => { setRefreshKey((k) => k + 1); setTimeout(() => setRefreshKey((k) => k + 1), 2500); }} />}
        <IncomingFiles recordId={recordId} selects={selects} refreshKey={refreshKey} onFiled={() => { load(true); setRefreshKey((k) => k + 1); setTimeout(() => setRefreshKey((k) => k + 1), 1200); }} />
        <DocumentCheckPanel recordId={recordId} c={c} onChanged={() => { load(true); setRefreshKey((k) => k + 1); }} onRequestDocs={() => setReqSignal((n) => n + 1)} />
        <AllFiles recordId={recordId} refreshKey={refreshKey} onChanged={() => load(true)} />
      </div>

      <div className="col-span-12 lg:col-span-4">
        <DocsSidebar c={c} recordId={recordId} saveField={saveField} canEdit={canEdit} />
      </div>
    </div>
  );
}

function DocsSidebar({ c, recordId, saveField, canEdit }) {
  const [pwd, setPwd] = useState({});
  const folder = Array.isArray(c.drive_folder_link) ? c.drive_folder_link[0]?.url : c.drive_folder_link;

  const reveal = async (which) => {
    try {
      const { data } = await api.get(`/cases/${recordId}/reveal`, { params: { field: which } });
      setPwd((p) => ({ ...p, [which]: data.value ?? "(empty)" }));
    } catch (e) { if (e?.response?.status === 403) setPwd((p) => ({ ...p, [which]: "•••• hidden in preview" })); else toast.error("Reveal failed"); }
  };

  return (
    <div className="space-y-6">
      {c.sanction_letters?.length > 0 && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm">
          <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>Sanction letters</h3>
          <div className="space-y-2">
            {c.sanction_letters.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#38A2FF] hover:underline">
                <FileText className="w-4 h-4" /> {s.name || `Letter ${i + 1}`}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm">
        <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>Drive folder</h3>
        {folder ? (
          <a href={folder} target="_blank" rel="noreferrer" data-testid="drive-folder-button"
            className="inline-flex items-center gap-1.5 text-sm bg-[#222222] text-white px-4 py-2 rounded-lg hover:bg-[#333333] transition-colors font-medium">
            <FolderOpen className="w-4 h-4" /> Open folder
          </a>
        ) : (
          <p className="text-sm text-[#6B7280]">No folder linked yet.</p>
        )}
        {canEdit && (
          <div className="mt-3">
            <EditableField label="Drive Folder Link (paste URL)" value={folder} fieldId={FF.DRIVE_FOLDER_LINK || "fldHjILefIlMNZMbL"} editable={true} onSave={saveField} testid="drive-link-edit" />
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm">
        <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>Sensitive passwords</h3>
        {[["bank", "Bank Statement Password", c.has_bank_stmt_pwd], ["salary", "Salary Slip Password", c.has_salary_slip_pwd]].map(([k, label, has]) => (
          <div key={k} className="flex items-center justify-between bg-[#F7F7F7] px-3 py-1.5 rounded-lg border border-[#E2E2E2] text-xs font-mono mb-2">
            <span className="text-[#6B7280]">{label}</span>
            {pwd[k] !== undefined ? (
              <span className="text-[#333333] font-semibold" data-testid={`pwd-value-${k}`}>{pwd[k]}</span>
            ) : has ? (
              <button onClick={() => reveal(k)} data-testid={`reveal-password-${k}`} className="flex items-center gap-1 text-[#FF4802] hover:underline">
                <Eye className="w-3.5 h-3.5" /> Reveal
              </button>
            ) : <span className="tracking-widest text-[#C5C4C4]">— none —</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileSelect({ label, value, options, onPick, disabled, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase text-[#6B7280] mb-1.5" style={{ letterSpacing: "0.06em" }}>{label}</label>
      <Select value={value || ""} onValueChange={onPick} disabled={disabled}>
        <SelectTrigger data-testid={testid} className="border-[#E2E2E2] rounded-lg bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ApplicantSection({ title, items, recordId, canEdit, onChanged, reload }) {
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-[#F7F7F7] border-b border-[#E2E2E2]">
        <h3 className="text-sm font-medium text-[#333333]">{title}</h3>
      </div>
      <div className="divide-y divide-[#F7F7F7]">
        {items.map((it) => (
          <DocRow key={it.entry_id + it.applicant} item={it} recordId={recordId} canEdit={canEdit} onChanged={onChanged} reload={reload} />
        ))}
      </div>
    </div>
  );
}

function DocRow({ item, recordId, canEdit, onChanged, reload }) {
  const [flagOpen, setFlagOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const meta = STATUS_META[item.status] || STATUS_META.missing;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("field_id", item.field_id);
      fd.append("doc_key", item.key);
      fd.append("applicant", item.applicant);
      await api.post(`/cases/${recordId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Filed to Airtable");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const markReceived = async () => {
    try {
      const { data } = await api.post(`/cases/${recordId}/documents/mark-received`,
        { entry_id: item.entry_id, applicant: item.applicant, custom: item.custom });
      onChanged(data);
      toast.success("Marked received");
    } catch (e) { toast.error("Failed"); }
  };

  const view = async () => {
    const win = window.open("", "_blank"); // open synchronously so popup blockers allow it
    try {
      const { data } = await api.get(`/cases/${recordId}/document`, { params: { field: item.field_id || undefined, entry_id: item.entry_id, applicant: item.applicant } });
      if (data.url && win) win.location.href = `${BACKEND}${data.url}`;
      else if (data.url) window.open(`${BACKEND}${data.url}`, "_blank");
    } catch (e) { win?.close(); toast.error(e?.response?.data?.detail || "Could not open"); }
  };

  const removeCustom = async () => {
    try {
      const { data } = await api.post(`/cases/${recordId}/documents/custom/remove`, { entry_id: item.entry_id, applicant: item.applicant, custom: true });
      onChanged(data);
      toast.success("Removed");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`doc-item-${item.entry_id}-${item.applicant}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#333333]">{item.label}</span>
          {item.rm_check && (
            <span title={item.rm_check} className="text-[#C5C4C4] hover:text-[#6B7280] cursor-help">
              <Info className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
        {item.reason && (item.status === "needs_reupload" || item.status === "reuploaded_check" || item.status === "partial") && (
          <div className="text-xs text-[#CA1212] mt-0.5">Reason: {item.reason}</div>
        )}
        {item.attachment_count > 0 && (
          <div className="text-xs text-[#6B7280] mt-0.5">{item.attachment_count} file{item.attachment_count > 1 ? "s" : ""}</div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${meta.cls}`} data-testid={`status-${item.entry_id}-${item.applicant}`}>{meta.label}</span>
        {item.attachment_count > 0 && (
          <button onClick={view} title="View" data-testid={`view-${item.entry_id}-${item.applicant}`} className="p-1.5 rounded-lg border border-[#E2E2E2] hover:bg-[#F7F7F7] text-[#6B7280]"><Eye className="w-4 h-4" /></button>
        )}
        {canEdit && !item.custom && item.field_id && (
          <>
            <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid={`file-${item.entry_id}-${item.applicant}`} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} title="Upload / file"
              data-testid={`upload-${item.entry_id}-${item.applicant}`}
              className="p-1.5 rounded-lg border border-[#E2E2E2] hover:bg-[#FF4802]/5 text-[#FF4802] disabled:opacity-50">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            </button>
          </>
        )}
        {canEdit && (item.status === "reuploaded_check" || (item.custom && item.status !== "received")) && (
          <button onClick={markReceived} data-testid={`mark-received-${item.entry_id}-${item.applicant}`}
            className="flex items-center gap-1 text-xs bg-[#159A50] text-white px-2.5 py-1.5 rounded-lg hover:opacity-90"><Check className="w-3.5 h-3.5" /> Received</button>
        )}
        {canEdit && (
          <button onClick={() => setFlagOpen(true)} title="Needs re-upload" data-testid={`flag-${item.entry_id}-${item.applicant}`}
            className="p-1.5 rounded-lg border border-[#E2E2E2] hover:bg-[#CA1212]/5 text-[#CA1212]"><Flag className="w-4 h-4" /></button>
        )}
        {canEdit && item.custom && (
          <button onClick={removeCustom} title="Remove custom item" data-testid={`remove-${item.entry_id}-${item.applicant}`}
            className="p-1.5 rounded-lg border border-[#E2E2E2] hover:bg-[#CA1212]/5 text-[#CA1212]"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>

      <FlagDialog open={flagOpen} onOpenChange={setFlagOpen} item={item} recordId={recordId} onChanged={onChanged} />
    </div>
  );
}

function FlagDialog({ open, onOpenChange, item, recordId, onChanged }) {
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setReason(""); setCustomReason(""); } }, [open]);
  const reasons = item.custom ? ["Password needed", "Unreadable / blurry", "Wrong document", "Not the latest"] : (item.reupload_reasons || []);

  const submit = async () => {
    const r = reason === "__custom" ? customReason.trim() : reason;
    if (!r) { toast.error("Pick or type a reason"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${recordId}/documents/flag`,
        { entry_id: item.entry_id, applicant: item.applicant, reason: r, field_id: item.field_id, custom: item.custom });
      onChanged(data);
      onOpenChange(false);
      toast.success("Flagged for re-upload");
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl truva-popup-shadow" aria-describedby={undefined} data-testid="flag-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">Needs re-upload — {item.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="flag-reason-select" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
            <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
              {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              <SelectItem value="__custom">Other (type below)</SelectItem>
            </SelectContent>
          </Select>
          {reason === "__custom" && (
            <Textarea value={customReason} onChange={(e) => setCustomReason(e.target.value)} placeholder="Type the reason" data-testid="flag-custom-reason" className="border-[#E2E2E2] rounded-lg" />
          )}
        </div>
        <DialogFooter className="gap-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm text-[#333333] hover:bg-[#F7F7F7]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="flag-confirm" className="px-4 py-2 rounded-lg bg-[#CA1212] text-white text-sm hover:opacity-90 disabled:opacity-50 font-medium">
            {saving ? "…" : "Flag for re-upload"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCustom({ recordId, hasCo, isDirector, onAdded }) {
  const [label, setLabel] = useState("");
  const [applicant, setApplicant] = useState("borrower");
  const [saving, setSaving] = useState(false);
  const applicants = ["borrower", ...(hasCo ? ["coborrower"] : []), ...(isDirector ? ["company"] : [])];

  const add = async () => {
    if (!label.trim()) { toast.error("Enter a label"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${recordId}/documents/custom`, { label: label.trim(), applicant });
      onAdded(data);
      setLabel("");
      toast.success("Custom item added");
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-dashed border-[#E2E2E2] bg-white p-5 shadow-sm" data-testid="add-custom">
      <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>Add a custom item (bank query, one-off)</h3>
      <div className="flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Explanation letter for employment gap"
          data-testid="custom-label" className="border-[#E2E2E2] rounded-lg flex-1" />
        <Select value={applicant} onValueChange={setApplicant}>
          <SelectTrigger data-testid="custom-applicant" className="border-[#E2E2E2] rounded-lg w-40"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
            {applicants.map((a) => <SelectItem key={a} value={a}>{APPLICANT_LABEL[a]}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={add} disabled={saving} data-testid="custom-add" className="flex items-center gap-1.5 px-4 rounded-lg bg-[#FF4802] text-white text-sm hover:bg-[#E2521B] disabled:opacity-50 font-medium">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
    </div>
  );
}
